import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Popup from "reactjs-popup";

import { useMainStore } from "@/contexts/Store";

import pointsIconActive from "../../../images/table-image/icon/points-icon-active.svg";
import pointsIcon from "../../../images/table-image/icon/points-icon.svg";
import { getRecordName } from "../../helpers/nameForThemisModuleIdentifier";
import SlideMenu from "../../slideMenu/SlideMenu";
import ConfirmationDialog from "../shared/ConfirmationDialog";
import MoveToSectionPopup from "../shared/MoveToSectionPopup";
import SendRecordVersion from "../shared/SendRecordVersion";

type Props = {
  recordID: number;
  recordVersionID: number;
  isPlanStarted?: boolean;
};

function QAContextMenu({ recordVersionID, isPlanStarted }: Props) {
  // Import MobX stores
  const mainStore = useMainStore();

  // Hooks
  const history = useHistory();

  // State
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [showUnlockPopup, setShowUnlockPopup] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(true);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isArchiveConfirmationOpen, setIsArchiveConfirmationOpen] =
    useState(false);
  const [showSlideMenu, setShowSlideMenu] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [viewPopup, setViewPopup] = useState("menu");

  // Variables
  const { canDeleteRecords, hasModuleWriteAccess } = mainStore.userPermissions;
  const activeWorkspace = mainStore.users.user.active_workspace;
  const buttonClasses = classNames("points-button", {
    "big-active": isOpenModal,
  });
  const listPointsClasses = classNames("list-points");
  const recordVersion = mainStore.recordVersions.list.find(
    (rv) => rv.id === recordVersionID,
  );
  const statusValue = mainStore.avroSchemas.valueForField(
    "status",
    recordVersion?.data,
  )?.[0];
  const { isIW: isInternal, workspaceID } = mainStore.context;
  const inProgress = statusValue === "in_progress";
  const pendingFinalization = statusValue === "pending_finalization";
  const completed = statusValue === "completed";
  const moduleWorkspaces = mainStore.moduleWorkspaces.list;
  const recordName = getRecordName(
    "qa_tests_development",
    moduleWorkspaces,
    true,
  );

  // Effects
  useEffect(() => {
    if (!showPopup) {
      setViewPopup("menu");
    }
  }, [showPopup]);

  // Functions
  // @ts-expect-error TS(7006) FIXME: Parameter 'rvID' implicitly has an 'any' type.
  const handleMove = async (rvID, sectionTagID) => {
    await mainStore.qa.updateSection(rvID, sectionTagID);
  };

  function noButtonPressed() {
    handlePopUpClose();
    setIsDropdownOpen(false);
  }

  function deleteRowConfirmation() {
    setIsDropdownOpen(false);
    setIsDeleteConfirmationOpen(true);
  }

  function archiveRowConfirmation() {
    setIsDropdownOpen(false);
    setIsArchiveConfirmationOpen(true);
  }

  function deleteRow() {
    // Delete RecordVersion
    const { company } = mainStore.companies;
    if (company && company.id) {
      mainStore.qa.delete(recordVersionID);
      mainStore.toast.setText("Test has been deleted!");
    }
  }

  function archiveRow() {
    const { company } = mainStore.companies;
    if (company && company.id) {
      // @ts-expect-error TS(2339) FIXME: Property 'archive' does not exist on type 'QA'.
      mainStore.qa.archive(recordVersionID);
      mainStore.toast.setText("Test has been archived!");
    }
  }

  function duplicateRecord() {
    mainStore.recordVersions.duplicate(recordVersionID);
    setIsOpenModal(false);
  }

  function handlePopUpOpen() {
    setIsOpenModal(true);
  }

  function handlePopUpClose() {
    setIsDropdownOpen(true);
    setIsDeleteConfirmationOpen(false);
    setIsOpenModal(false);
    setViewPopup("menu");
  }

  function closeSlideMenu() {
    setShowSlideMenu(false);
  }

  function openSendSlideMenu() {
    setShowSlideMenu(true);
    setIsOpenModal(false);
  }

  function goToTestDetails() {
    history.push(
      `/workspaces/${workspaceID}/modules/qa-tests-development/${recordVersionID}`,
    );
  }

  function shareRecordVersion(recordVersionId: number, workspaceIds: number[]) {
    return mainStore.recordVersions.share(
      recordVersionId,
      "RecordVersion",
      workspaceIds,
    );
  }

  const completeQA = () => mainStore.qa.completeQATest(recordVersionID);

  const unlockQA = () => mainStore.qa.unlockQATest(recordVersionID);

  const renderTrigger = (
    <button
      className={buttonClasses}
      data-testid="points-button-trigger"
      data-tooltip-id="tooltip"
      data-tooltip-content="More Options"
      data-tooltip-place="bottom"
    >
      {isInternal && hasModuleWriteAccess && (
        <>
          <img className="points-icon" src={pointsIcon} alt="points-icon" />
          <img
            className="points-icon-active"
            src={pointsIconActive}
            alt="points-icon-active"
          />
        </>
      )}
    </button>
  );

  const renderBaseContent = (
    <div className="table-dropdown policy-dropdown">
      <ul>
        {(completed || inProgress || pendingFinalization) && (
          <li data-testid="view-detail" onClick={goToTestDetails}>
            View
          </li>
        )}
        {activeWorkspace?.is_internal && (
          <li data-testid="share-test-trigger" onClick={openSendSlideMenu}>
            Share Test
          </li>
        )}
        {!completed && (
          <li
            className={classNames({
              // @ts-expect-error TS(2339) FIXME: Property 'length' does not exist on type 'SectionT... Remove this comment to see the full error message
              disabled: mainStore.sectionTags.length === 0,
            })}
            onClick={() => setViewPopup("move")}
            data-testid="move-button-trigger"
          >
            Move to
          </li>
        )}
        {hasModuleWriteAccess && (
          <li data-testid="duplicate-test-trigger" onClick={duplicateRecord}>
            Duplicate
          </li>
        )}
        {isPlanStarted && (
          <li
            data-testid="archive-test-trigger"
            onClick={archiveRowConfirmation}
          >
            Archive
          </li>
        )}
        {mainStore.context.activeWorkspace?.is_internal && canDeleteRecords && (
          <>
            <hr />
            <li
              data-testid="delete-test-trigger"
              onClick={deleteRowConfirmation}
            >
              Delete
            </li>
          </>
        )}
      </ul>
    </div>
  );

  const renderUnlockPopupTrigger = (
    <div>
      <button
        className={classNames("unlock table-button", {
          "big-active": showUnlockPopup,
        })}
        onClick={() => setShowUnlockPopup(true)}
        data-testid="qa-context-menu-unlock-trigger"
        disabled={!hasModuleWriteAccess}
      >
        Unlock
      </button>
    </div>
  );

  const renderUnlockPopup = (
    <Popup
      position="bottom right"
      trigger={renderUnlockPopupTrigger}
      open={showUnlockPopup}
      onOpen={() => setShowUnlockPopup(true)}
      onClose={() => setShowUnlockPopup(false)}
      keepTooltipInside
    >
      <div className="table-dropdown">
        <ConfirmationDialog
          heading="Unlock Test"
          content="Are you sure you wish to unlock this Test?"
          handleConfirm={unlockQA}
          handleReject={() => setShowUnlockPopup(false)}
        />
      </div>
    </Popup>
  );

  const renderButton = () => {
    if (!isInternal) {
      return (
        <button
          className="finalize-active"
          data-testid="qa-test-view-button"
          onClick={goToTestDetails}
        >
          View
        </button>
      );
    }

    if (pendingFinalization) {
      return (
        <button
          className="approve"
          onClick={completeQA}
          disabled={!hasModuleWriteAccess}
        >
          Complete
        </button>
      );
    } else if (completed) {
      return renderUnlockPopup;
    }

    return (
      <button
        className="finalize-active"
        data-testid="qa-test-view-button"
        onClick={goToTestDetails}
      >
        View
      </button>
    );
  };

  return (
    <div className={listPointsClasses}>
      {renderButton()}
      <Popup
        position="bottom right"
        trigger={renderTrigger}
        open={isOpenModal}
        onOpen={handlePopUpOpen}
        onClose={handlePopUpClose}
        keepTooltipInside
      >
        {viewPopup === "move" && (
          <MoveToSectionPopup
            moduleName="Tests"
            recordVersionID={recordVersionID}
            onMove={handleMove}
            onClose={() => setShowPopup(false)}
            omitTopLevelSection
          />
        )}
        {isDropdownOpen && viewPopup === "menu" && renderBaseContent}
        {isDeleteConfirmationOpen && (
          <div className="table-dropdown success-dropdown">
            <ConfirmationDialog
              heading="Delete Test"
              content="Are you sure you want to delete this test? This is not reversible."
              handleConfirm={deleteRow}
              handleReject={noButtonPressed}
            />
          </div>
        )}
        {isArchiveConfirmationOpen && (
          <div className="table-dropdown success-dropdown">
            <ConfirmationDialog
              heading="Archive Test"
              content="Are you sure you want to archive this test?"
              handleConfirm={archiveRow}
              handleReject={noButtonPressed}
            />
          </div>
        )}
      </Popup>
      <SlideMenu open={showSlideMenu} closeSlideMenu={closeSlideMenu}>
        <SendRecordVersion
          recordVersionID={recordVersionID}
          recordName={recordName}
          sendFunction={shareRecordVersion}
        />
      </SlideMenu>
    </div>
  );
}

export default observer(QAContextMenu);
